import type {
  MediaEdge,
  MediaImage,
} from '@shopify/hydrogen/storefront-api-types';
// @ts-ignore
import {Pagination, Navigation, SwiperClass} from 'swiper';
// @ts-ignore
import {Swiper, SwiperSlide} from 'swiper/react';
import {useEffect, useMemo, useState} from 'react';
import {ProductGalleryModal} from './ProductGalleryModal.client';
import {ProductGalleryAsset} from './ProductGalleryAsset.client';
import {useProductOptions} from '@shopify/hydrogen';
import {DATA_TEST_IDS} from '~/lib/const';
import {useMedia} from 'react-use';

function MobileGalleryCarousel({media}: {media: MediaEdge['node'][]}) {
  const [swiper, setSwiper] = useState<SwiperClass>({});

  useEffect(() => {
    setTimeout(() => {
      swiper?.slideTo?.(0);
    }, 10);
    // We don't want to re-run this effect when the swiper instance changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media]);

  return (
    <Swiper
      onSwiper={(s) => setSwiper(s)}
      // @ts-expect-error @headlessui/react
      modules={[Navigation, Pagination]}
      pagination={{
        clickable: true,
        horizontalClass: 'flex justify-center items-center p-4',
        bulletActiveClass: `!bg-black`,
        bulletClass: 'w-2 h-2 rounded mx-1 inline-block bg-gray-250',
      }}
      className={'relative'}
      slidesPerView={'auto'}
      spaceBetween={10}
      effect="fade"
      shortSwipes={true}
      threshold={10}
      allowTouchMove={true}
    >
      {media?.map((med, index) => (
        <SwiperSlide
          // @ts-ignore
          key={med.id || med.image.id}
          style={{
            width: 'calc(100vw - 40px)',
          }}
        >
          <div className={'flex'}>
            <ProductGalleryAsset
              media={med}
              index={index}
              videoNotInteractive={false}
              videoControls={1}
            />
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

/**
 * A client component that defines a media gallery for hosting images, 3D models, and videos of products
 */
export function ProductGallery({
  media,
  className,
  children,
}: {
  media: MediaEdge['node'][];
  className?: string;
  children?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const isWide = useMedia('(min-width: 768px)');

  const [isMobile, setIsMobile] = useState(true);
  const [isDesktop, setIsDesktop] = useState(true);

  const {selectedVariant} = useProductOptions();

  const medias = useMemo(
    () =>
      selectedVariant && selectedVariant.image
        ? [
            {
              id: selectedVariant.image.id,
              mediaContentType: 'IMAGE',
              image: selectedVariant.image,
            } as MediaImage,
            ...media.filter(
              (med) =>
                med.mediaContentType !== 'IMAGE' ||
                (med.mediaContentType === 'IMAGE' &&
                  'image' in med &&
                  med.image?.url !== selectedVariant.image?.url),
            ),
          ]
        : media,
    [media, selectedVariant],
  );

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    setIsDesktop(window.innerWidth >= 768);
  }, [isWide]);

  if (!media.length) {
    return null;
  }

  return (
    <>
      {isMobile && (
        <div className={'block w-[calc(100vw_-_20px)] md:hidden'}>
          <MobileGalleryCarousel media={medias} />
        </div>
      )}

      {isDesktop && (
        <div
          data-testid={DATA_TEST_IDS.DESKTOP_GALLERY}
          className={`hidden md:inline-grid swimlane mt-[17px] gap-[10px] lg:gap-[38px] md:grid-flow-row hiddenScroll pl-0 md:p-0 md:overflow-x-auto md:grid-cols-2 ${className}`}
        >
          {medias.map((med, index) => {
            return (
              <ProductGalleryAsset
                // @ts-ignore
                key={med.id || med.image.id}
                index={index}
                media={med}
                onClick={() => {
                  setOpen(true);
                  setActiveIndex(index);
                }}
              />
            );
          })}

          {children}
        </div>
      )}

      {open && (
        <ProductGalleryModal
          open={open}
          onCloseModal={() => setOpen(false)}
          media={medias}
          index={activeIndex}
        />
      )}
    </>
  );
}
